import {
  chessStreakProgress,
  practiceDate,
  practiceTimezone,
  shiftPracticeDate,
  learningLessons,
  foundationLessonId,
  completedExerciseIndices,
  openingLessonManifest,
  recommendedLearningLesson,
} from "@chesshotel/types";
import { StatusTypes } from "@chesshotel/constants";
import {
  getAllGuestLessonProgress,
  guestLessonsPersisted,
} from "../lessons/guestLessonProgress";
import { browserPracticeTimezone } from "../puzzles/dailyPractice";
import { refreshLearning } from "./learningEvents";
import type { LearningOverview } from "./learningTypes";
import {
  getGuestStartingPoint,
  guestStartingPointPersisted,
} from "./learningStartingPoint";

export const GUEST_ACTIVITY_KEY = "chesshotel:guest-activity:v1";
type Activity = { date: string; key: string; learning: boolean };
type GuestActivity = { timezone: string; activities: Activity[] };
let volatile: GuestActivity | null = null;
let persistent = true;
const validDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const date = new Date(`${value}T12:00:00Z`);
  return (
    Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

function read(): GuestActivity {
  if (volatile && !persistent) return volatile;
  const empty = () => ({
    timezone: practiceTimezone(browserPracticeTimezone()),
    activities: [],
  });
  if (typeof window === "undefined") return empty();
  try {
    const raw = window.localStorage.getItem(GUEST_ACTIVITY_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (!stored || !Array.isArray(stored.activities)) return empty();
    return {
      timezone: practiceTimezone(stored.timezone),
      activities: stored.activities
        .filter(
          (item: Activity) =>
            item &&
            validDate(item.date) &&
            typeof item.key === "string" &&
            item.key.length <= 180 &&
            typeof item.learning === "boolean",
        )
        .slice(-20000),
    };
  } catch {
    // Corrupt or blocked storage must never interrupt chess.
    try {
      window.localStorage.getItem(GUEST_ACTIVITY_KEY);
    } catch {
      persistent = false;
    }
    return volatile || empty();
  }
}

export function recordGuestActivity(
  kind: "game" | "puzzle" | "lesson",
  identity: string,
  now = new Date(),
) {
  const store = read();
  const date = practiceDate(now, store.timezone);
  const key = `${kind}:${identity}`.slice(0, 180);
  if (
    !store.activities.some(
      (item) => item.key === key && (kind === "game" || item.date === date),
    )
  ) {
    store.activities.push({ date, key, learning: kind !== "game" });
    store.activities = store.activities.slice(-20000);
    volatile = store;
    try {
      window.localStorage.setItem(GUEST_ACTIVITY_KEY, JSON.stringify(store));
      persistent = true;
    } catch {
      persistent = false;
    }
  }
  refreshLearning();
}

/** Results shown while spectating or replaying a game cannot earn guest attendance. */
export function recordGuestGame(
  input: {
    gameId: string | number;
    status: number;
    moves: unknown[] | null;
    displayGame: boolean;
  },
  now = new Date(),
) {
  if (
    input.displayGame ||
    !input.gameId ||
    !input.moves?.length ||
    ![
      StatusTypes.TIME_UP,
      StatusTypes.QUIT,
      StatusTypes.CHECK_MATE,
      StatusTypes.RESIGN,
      StatusTypes.STALE_MATE,
      StatusTypes.DRAW,
      StatusTypes.THREEFOLD_REPETITION,
    ].includes(input.status)
  )
    return;
  recordGuestActivity("game", String(input.gameId), now);
}

export function getGuestLearningOverview(now = new Date()): LearningOverview {
  const store = read();
  const today = practiceDate(now, store.timezone);
  const activities = store.activities.filter((item) => item.date <= today);
  const chessStreak = {
    ...chessStreakProgress(
      activities.map((item) => item.date),
      today,
    ),
    timezone: store.timezone,
  };
  const learning = activities.filter((item) => item.learning);
  const attendance = chessStreakProgress(
    learning.map((item) => item.date),
    today,
  );
  const lessons = getAllGuestLessonProgress();
  const lessonProgress = learningLessons.map((lesson) => ({
    slug: lesson.slug,
    completedExercises: completedExerciseIndices(
      lessons[foundationLessonId(lesson.slug)]?.completedLines,
      lesson.exercises.length,
    ),
  }));
  const completed = learningLessons.filter(
    (lesson) =>
      lessonProgress.find((row) => row.slug === lesson.slug)!.completedExercises
        .length === lesson.exercises.length,
  );
  const startingPoint = getGuestStartingPoint();
  const starter = recommendedLearningLesson({
    progress: lessonProgress,
    startingPoint,
    establishedPlayer:
      activities.filter((item) => item.key.startsWith("game:")).length >= 10,
    hasOpeningProgress: Object.keys(openingLessonManifest).some(
      (slug) =>
        (lessons[slug]?.completedLines.length || 0) > 0 ||
        lessons[slug]?.practiceLineProgress.some((n) => n > 0),
    ),
  });
  const firstLesson = completed
    .map((lesson) => lessons[foundationLessonId(lesson.slug)]?.updatedAt)
    .filter((value): value is number => Number.isFinite(value))
    .sort((a, b) => a - b)[0];
  const revisit = completed.find((lesson) => {
    const updated = lessons[foundationLessonId(lesson.slug)]?.updatedAt;
    return updated && practiceDate(new Date(updated), store.timezone) < today;
  });
  const weekStart = chessStreak.week[0].date;
  const weeklyCurrent = new Set(
    learning.filter((item) => item.date >= weekStart).map((item) => item.key),
  ).size;
  return {
    guest: true,
    persistent:
      persistent && guestLessonsPersisted() && guestStartingPointPersisted(),
    startingPoint,
    revisitLessonSlug: revisit?.slug,
    chessStreak,
    practice: {
      ...attendance,
      totalSessions: learning.length,
      totalPersonalSessions: 0,
      nextPracticeDate: learning.length ? shiftPracticeDate(today, 1) : null,
      achievements: [
        {
          key: "first_foundation_lesson",
          target: 1,
          current: completed.length ? 1 : 0,
          unlocked: completed.length
            ? new Date(firstLesson || now.getTime()).toISOString()
            : null,
        },
      ],
    },
    nextStep: starter
      ? { kind: "lesson", lessonSlug: starter.slug }
      : { kind: "collection" },
    discoveries: [],
    weeklyGoal: {
      current: weeklyCurrent,
      target: 3,
      weekStart,
      completed: weeklyCurrent >= 3,
    },
    personalPositions: 0,
    rememberedPositions: 0,
    duePositions: 0,
    lessonsCompleted: completed.length,
    lessonsTotal: learningLessons.length,
    lessonProgress,
    openingProgress: Object.keys(openingLessonManifest).map((slug) => ({
      slug,
      completedLines: lessons[slug]?.completedLines || [],
      completedChallengeLines: lessons[slug]?.completedChallengeLines || [],
      practiceLineProgress: lessons[slug]?.practiceLineProgress || [],
      challengeLineProgress: lessons[slug]?.challengeLineProgress || [],
      challengeCompleted: lessons[slug]?.challengeCompleted || false,
    })),
  };
}

export function guestActivitySnapshot() {
  const store = read();
  return {
    timezone: store.timezone,
    activities: store.activities.map((item) => ({ ...item })),
  };
}
export function clearTransferredGuestActivity(
  snapshot: ReturnType<typeof guestActivitySnapshot>,
) {
  const store = read();
  const imported = new Set(
    snapshot.activities.map((item) => JSON.stringify(item)),
  );
  store.activities = store.activities.filter(
    (item) => !imported.has(JSON.stringify(item)),
  );
  volatile = store;
  try {
    window.localStorage.setItem(GUEST_ACTIVITY_KEY, JSON.stringify(store));
    persistent = true;
  } catch {
    persistent = false;
  }
  refreshLearning();
}
